import { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    Container,
    Grid,
    Card,
    CardContent,
    Typography,
    Box,
    Chip,
    CircularProgress,
    Alert,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Snackbar,
} from '@mui/material'
import {
    Dashboard as DashboardIcon,
    Description as ApplicationIcon,
    CheckCircle as ApprovedIcon,
    Schedule as InProcessIcon,
    Cancel as DeclinedIcon,
    Assessment as AssessmentIcon,
} from '@mui/icons-material'
import { apiClient } from '../services/api'

interface Application {
    id: string
    description: string
    businessName: string
    brokerName: string | null
    brokerEmail: string | null
    statusId: number
    statusName: string
    createdDate: string
    submittedDate: string | null
}

const statusOptions = [
    { id: 1, name: 'Submitted', label: 'Submitted' },
    { id: 2, name: 'InProcess', label: 'In Process' },
    { id: 3, name: 'Underwriting', label: 'Underwriting' },
    { id: 4, name: 'Approved', label: 'Approved' },
    { id: 5, name: 'ConditionallyApproved', label: 'Conditionally Approved' },
    { id: 6, name: 'Declined', label: 'Declined' },
]

const getStatusColor = (
    statusId: number
): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    if (statusId === 4 || statusId === 5) return 'success' // Approved, Conditionally Approved
    if (statusId === 6) return 'error' // Declined
    if (statusId === 3) return 'warning' // Underwriting
    if (statusId === 2) return 'info' // InProcess
    if (statusId === 1) return 'primary' // Submitted
    return 'default'
}

const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    })
}

export default function DashboardPage() {
    const navigate = useNavigate()
    const [applications, setApplications] = useState<Application[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [selectedStatusFilter, setSelectedStatusFilter] = useState<number | null>(null)
    const [toastOpen, setToastOpen] = useState(false)
    const [toastMessage, setToastMessage] = useState('')
    const previousApplicationIdsRef = useRef<Set<string>>(new Set())

    useEffect(() => {
        fetchApplications()

        // Poll for new applications every 30 seconds
        const pollInterval = setInterval(() => {
            fetchApplications(true)
        }, 30000)

        return () => clearInterval(pollInterval)
    }, [])

    const fetchApplications = async (isPolling = false) => {
        try {
            if (!isPolling) {
                setLoading(true)
            }
            const data = await apiClient.getApplications()

            // Check for new applications if this is a polling request
            if (isPolling && previousApplicationIdsRef.current.size > 0) {
                const newApplications = data.filter(
                    (app: Application) => !previousApplicationIdsRef.current.has(app.id)
                )

                if (newApplications.length > 0) {
                    const message = newApplications.length === 1
                        ? `New application received: ${newApplications[0].businessName || 'Unknown'}`
                        : `${newApplications.length} new applications received`
                    setToastMessage(message)
                    setToastOpen(true)
                }
            }

            // Update previous IDs
            previousApplicationIdsRef.current = new Set(data.map((app: Application) => app.id))

            setApplications(data)
            setError(null)
        } catch (err) {
            setError('Failed to load applications')
            console.error(err)
        } finally {
            if (!isPolling) {
                setLoading(false)
            }
        }
    }

    const handleToastClose = () => {
        setToastOpen(false)
    }

    const handleRowClick = (id: string) => {
        navigate(`/applications/${id}`)
    }

    // Calculate statistics
    const totalApplications = applications.length
    const submittedCount = applications.filter(app => app.statusId === 1).length
    const inProcessCount = applications.filter(app => app.statusId === 2).length
    const underwritingCount = applications.filter(app => app.statusId === 3).length
    const approvedCount = applications.filter(app => app.statusId === 4 || app.statusId === 5).length
    const declinedCount = applications.filter(app => app.statusId === 6).length

    // Filter applications based on selected status
    const filteredApplications = selectedStatusFilter
        ? applications.filter(app => app.statusId === selectedStatusFilter)
        : applications

    // Get recent applications (last 10)
    const recentApplications = [...filteredApplications]
        .sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime())
        .slice(0, 10)

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Container maxWidth="xl">
            <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <DashboardIcon sx={{ fontSize: 32, mr: 1 }} />
                    <Typography variant="h4">Dashboard</Typography>
                </Box>
                <Typography variant="body1" color="text.secondary">
                    Overview of your lending applications
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={4}>
                    <Card
                        sx={{
                            cursor: 'pointer',
                            '&:hover': { boxShadow: 6 },
                            transition: 'box-shadow 0.3s',
                        }}
                        onClick={() => setSelectedStatusFilter(null)}
                    >
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography color="text.secondary" gutterBottom>
                                        Total Applications
                                    </Typography>
                                    <Typography variant="h3">{totalApplications}</Typography>
                                </Box>
                                <ApplicationIcon sx={{ fontSize: 48, color: 'primary.main', opacity: 0.3 }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                    <Card
                        sx={{
                            cursor: 'pointer',
                            '&:hover': { boxShadow: 6 },
                            transition: 'box-shadow 0.3s',
                        }}
                        onClick={() => setSelectedStatusFilter(2)}
                    >
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography color="text.secondary" gutterBottom>
                                        In Process
                                    </Typography>
                                    <Typography variant="h3">{inProcessCount}</Typography>
                                </Box>
                                <InProcessIcon sx={{ fontSize: 48, color: 'info.main', opacity: 0.3 }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                    <Card
                        sx={{
                            cursor: 'pointer',
                            '&:hover': { boxShadow: 6 },
                            transition: 'box-shadow 0.3s',
                        }}
                        onClick={() => setSelectedStatusFilter(3)}
                    >
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography color="text.secondary" gutterBottom>
                                        Underwriting
                                    </Typography>
                                    <Typography variant="h3">{underwritingCount}</Typography>
                                </Box>
                                <AssessmentIcon sx={{ fontSize: 48, color: 'warning.main', opacity: 0.3 }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                    <Card
                        sx={{
                            cursor: 'pointer',
                            '&:hover': { boxShadow: 6 },
                            transition: 'box-shadow 0.3s',
                        }}
                        onClick={() => setSelectedStatusFilter(4)}
                    >
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography color="text.secondary" gutterBottom>
                                        Approved
                                    </Typography>
                                    <Typography variant="h3">{approvedCount}</Typography>
                                </Box>
                                <ApprovedIcon sx={{ fontSize: 48, color: 'success.main', opacity: 0.3 }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                    <Card
                        sx={{
                            cursor: 'pointer',
                            '&:hover': { boxShadow: 6 },
                            transition: 'box-shadow 0.3s',
                        }}
                        onClick={() => setSelectedStatusFilter(6)}
                    >
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography color="text.secondary" gutterBottom>
                                        Declined
                                    </Typography>
                                    <Typography variant="h3">{declinedCount}</Typography>
                                </Box>
                                <DeclinedIcon sx={{ fontSize: 48, color: 'error.main', opacity: 0.3 }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                    <Card
                        sx={{
                            cursor: 'pointer',
                            '&:hover': { boxShadow: 6 },
                            transition: 'box-shadow 0.3s',
                        }}
                        onClick={() => setSelectedStatusFilter(1)}
                    >
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography color="text.secondary" gutterBottom>
                                        Submitted
                                    </Typography>
                                    <Typography variant="h3">{submittedCount}</Typography>
                                </Box>
                                <ApplicationIcon sx={{ fontSize: 48, color: 'primary.main', opacity: 0.3 }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Applications Table */}
            <Card>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">
                            {selectedStatusFilter
                                ? `${statusOptions.find(s => s.id === selectedStatusFilter)?.label} Applications`
                                : 'Recent Applications'}
                        </Typography>
                        {selectedStatusFilter && (
                            <Button size="small" onClick={() => setSelectedStatusFilter(null)}>
                                Clear Filter
                            </Button>
                        )}
                    </Box>

                    {recentApplications.length === 0 ? (
                        <Alert severity="info">No applications found</Alert>
                    ) : (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Description</TableCell>
                                        <TableCell>Business</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Created</TableCell>
                                        <TableCell>Submitted</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {recentApplications.map(application => (
                                        <TableRow
                                            key={application.id}
                                            hover
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() => handleRowClick(application.id)}
                                        >
                                            <TableCell>{application.description || 'N/A'}</TableCell>
                                            <TableCell>{application.businessName || 'N/A'}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={application.statusName}
                                                    color={getStatusColor(application.statusId)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>{formatDate(application.createdDate)}</TableCell>
                                            <TableCell>{formatDate(application.submittedDate)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button variant="outlined" onClick={() => navigate('/applications')}>
                            View All Applications
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            <Snackbar
                open={toastOpen}
                autoHideDuration={6000}
                onClose={handleToastClose}
                message={toastMessage}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            />
        </Container>
    )
}
